/**
 * parseSpreadsheet.js
 *
 * XLSX parsing, header detection, and data extraction utilities
 * for the matrix import wizard. Uses dynamic import('xlsx') to
 * keep the library out of the main bundle.
 */

/**
 * Parse a File object into a workbook with all sheet data.
 *
 * @param {File} file - The XLSX/XLS/CSV file to parse
 * @returns {Promise<{ workbook: object, sheetNames: string[], sheets: Record<string, any[][]> }>}
 */
export async function parseFile(file) {
  const XLSX = await import('xlsx');

  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const sheetNames = workbook.SheetNames;
  const sheets = {};

  for (const name of sheetNames) {
    const sheet = workbook.Sheets[name];
    sheets[name] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  }

  return { workbook, sheetNames, sheets };
}

/**
 * Get array-of-arrays data for a specific sheet.
 *
 * @param {object} workbook - The XLSX workbook object
 * @param {string} sheetName - Name of the sheet to read
 * @returns {Promise<any[][]>}
 */
export async function getSheetData(workbook, sheetName) {
  const XLSX = await import('xlsx');

  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    return [];
  }

  return XLSX.utils.sheet_to_json(sheet, { header: 1 });
}

/**
 * Heuristic auto-detection of column/row mapping from sheet data.
 *
 * Strategy:
 * 1. Header row: score each row 0-10 based on how "header-like" its cells are
 *    (multi-word text strings score higher; pure numbers/short tokens score lower).
 * 2. Qual end column: last non-empty cell in the detected header row -- overflow
 *    data columns beyond the header are excluded.
 * 3. Name column: leftmost column (0-4) with long text strings in most data rows.
 * 4. Email column: column with '@' in 30%+ of data rows.
 * 5. Qual start column: first column after name/email identity columns.
 *
 * @param {any[][]} sheetData - Array-of-arrays sheet data
 * @returns {{ headerRow: number, dataStartRow: number, nameColumn: number, emailColumn: number|null, qualStartColumn: number, qualEndColumn: number }}
 */
export function autoDetectMapping(sheetData) {
  if (!sheetData || sheetData.length === 0) {
    return {
      headerRow: 0,
      dataStartRow: 1,
      nameColumn: 0,
      emailColumn: null,
      qualStartColumn: 1,
      qualEndColumn: 0,
    };
  }

  // --- 1. Find header row using a "header-likeness" score ---
  // Headers are typically multi-word text labels, not numbers, short tokens
  // like "x", or date-like values. We score each candidate row and pick the best.
  const scanLimit = Math.min(sheetData.length, 11);
  let headerRow = 0;
  let bestHeaderScore = -1;

  for (let r = 0; r < scanLimit; r++) {
    const row = sheetData[r] || [];
    if (row.length === 0) continue;

    let score = 0;
    let nonEmptyCount = 0;

    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      if (val == null || String(val).trim() === '') continue;
      nonEmptyCount++;

      const str = String(val).trim();

      // Strong header signals: multi-word text, longer than 3 chars, not a number
      if (typeof val === 'string' && str.length > 3 && isNaN(Number(str))) {
        // Contains spaces = likely a descriptive header label
        if (str.includes(' ')) {
          score += 3;
        } else {
          // Single word but long text (e.g. "Name", "Email", "PASMA")
          score += 2;
        }
      } else if (typeof val === 'string' && str.length <= 3 && isNaN(Number(str))) {
        // Short text tokens like "x", "/", "NA" → looks like data, not a header
        score -= 1;
      } else if (typeof val === 'number') {
        // Numbers are rarely headers (likely Excel serial dates or IDs)
        score -= 2;
      }
    }

    // Bonus: rows with many non-empty cells are more likely to be headers
    // but only if the cells actually look header-like (score > 0)
    if (score > 0) {
      score += nonEmptyCount;
    }

    // Slight preference for earlier rows (headers tend to be at the top)
    score -= r * 0.5;

    if (score > bestHeaderScore) {
      bestHeaderScore = score;
      headerRow = r;
    }
  }

  const dataStartRow = headerRow + 1;
  const headerCells = sheetData[headerRow] || [];

  // Find the last non-empty header column index
  let lastHeaderCol = 0;
  for (let c = 0; c < headerCells.length; c++) {
    if (headerCells[c] != null && String(headerCells[c]).trim() !== '') {
      lastHeaderCol = c;
    }
  }

  // --- 2. Determine data rows for analysis (use all rows up to ~50) ---
  const allDataRows = sheetData.slice(dataStartRow);
  // Filter out completely empty rows for analysis
  const nonEmptyDataRows = allDataRows.filter(
    (row) => row && row.some((cell) => cell != null && String(cell).trim() !== '')
  );
  const sampleSize = Math.min(nonEmptyDataRows.length, 50);
  const sampleRows = nonEmptyDataRows.slice(0, sampleSize);

  // --- 3. Find name column: leftmost column with long text in most data rows ---
  // Names are multi-character text strings, not "x" or "/" or numbers
  let nameColumn = 0;
  let bestNameScore = 0;

  const nameCandidateLimit = Math.min(lastHeaderCol + 1, 5);

  for (let c = 0; c < nameCandidateLimit; c++) {
    let score = 0;
    for (const row of sampleRows) {
      const val = row?.[c];
      if (val == null) continue;
      const str = String(val).trim();
      // Name-like: text, longer than 2 chars, not a number, contains a space or is long
      if (str.length > 2 && isNaN(Number(str)) && typeof val !== 'number') {
        score += str.includes(' ') ? 2 : 1; // Full names with spaces score higher
      }
    }
    if (score > bestNameScore) {
      bestNameScore = score;
      nameColumn = c;
    }
  }

  // --- 4. Find email column: column containing cells with '@' ---
  let emailColumn = null;

  for (let c = 0; c <= lastHeaderCol; c++) {
    if (c === nameColumn) continue;
    let atCount = 0;
    for (const row of sampleRows) {
      const val = row?.[c];
      if (val != null && String(val).includes('@')) {
        atCount++;
      }
    }
    // If at least 30% of non-empty sample rows have '@', treat it as email
    if (atCount > 0 && sampleSize > 0 && atCount >= sampleSize * 0.3) {
      emailColumn = c;
      break;
    }
  }

  // --- 5. Determine qual column range ---
  // Quals start after the last of the name/email identity columns
  const identityColumns = [nameColumn];
  if (emailColumn !== null) {
    identityColumns.push(emailColumn);
  }
  const qualStartColumn = Math.max(...identityColumns) + 1;
  const qualEndColumn = lastHeaderCol;

  return {
    headerRow,
    dataStartRow,
    nameColumn,
    emailColumn,
    qualStartColumn,
    qualEndColumn,
  };
}

/**
 * Extract structured data from sheet data given a mapping configuration.
 *
 * @param {any[][]} sheetData - Array-of-arrays sheet data
 * @param {{ headerRow: number, dataStartRow: number, nameColumn: number, emailColumn: number|null, qualStartColumn: number, qualEndColumn: number }} mapping
 * @returns {{ users: Array<{ rawName: string, email: string|null, rowIndex: number }>, qualifications: Array<{ rawName: string, colIndex: number }>, cells: Array<{ rowIndex: number, colIndex: number, rawValue: any, userName: string, qualName: string }> }}
 */
export function extractMappedData(sheetData, mapping) {
  const {
    headerRow,
    dataStartRow,
    nameColumn,
    emailColumn,
    qualStartColumn,
    qualEndColumn,
  } = mapping;

  const headerCells = sheetData[headerRow] || [];

  // --- Build qualifications list from header row ---
  const qualifications = [];
  for (let c = qualStartColumn; c <= qualEndColumn; c++) {
    const rawName = headerCells[c] != null ? String(headerCells[c]).trim() : '';
    if (rawName !== '') {
      qualifications.push({ rawName, colIndex: c });
    }
  }

  // --- Build users list and cells from data rows ---
  const users = [];
  const cells = [];

  for (let r = dataStartRow; r < sheetData.length; r++) {
    const row = sheetData[r] || [];

    // Extract name
    const rawName = row[nameColumn] != null ? String(row[nameColumn]).trim() : '';

    // Skip entirely empty rows
    if (rawName === '') continue;

    // Extract email
    let email = null;
    if (emailColumn !== null) {
      const emailVal = row[emailColumn];
      if (emailVal != null && String(emailVal).trim() !== '') {
        email = String(emailVal).trim();
      }
    }

    users.push({ rawName, email, rowIndex: r });

    // Extract cell values for each qualification column
    for (const qual of qualifications) {
      const rawValue = row[qual.colIndex];
      if (rawValue != null && String(rawValue).trim() !== '') {
        cells.push({
          rowIndex: r,
          colIndex: qual.colIndex,
          rawValue,
          userName: rawName,
          qualName: qual.rawName,
        });
      }
    }
  }

  return { users, qualifications, cells };
}
